// Deadline helpers shared by every surface that shows a task: the contact
// dialog, the board card, the agenda table and the CRM. Whether a task is LATE
// comes from the server (`overdue`); what is here is only how to say it.
import { cva } from "class-variance-authority"

// The hour a deadline picked without a time lands on. Mirrors the backend's
// OUTREACH_NOTE_DEFAULT_DUE_HOUR: a task due at midnight reads as "yesterday"
// to whoever opens the board in the morning.
export const DEFAULT_DUE_HOUR = 9
export const DEFAULT_DUE_TIME = "09:00"

const SUNDAY = 0
const SATURDAY = 6
// Business days left under which an upcoming date starts to warm up, then to
// turn urgent. The 2 mirrors the backend's OUTREACH_NOTE_DUE_SOON_DAYS.
const DUE_HEADS_UP_DAYS = 5
const DUE_SOON_DAYS = 2

// Weekends only, like backend/apps/autoleads/services/schedule.py: public
// holidays are deliberately out.
function isBusinessDay(day: Date): boolean {
  const weekday = day.getDay()
  return weekday !== SUNDAY && weekday !== SATURDAY
}

function startOfDay(moment: Date): Date {
  const day = new Date(moment)
  day.setHours(0, 0, 0, 0)
  return day
}

// Signed working days between today and the due day: 1 on a Friday for a task
// due Monday, -1 on a Monday for one due the Friday before, 0 for today (or a
// weekend day with no working day in between).
export function businessDaysUntil(due: string, now: Date = new Date()): number {
  const today = startOfDay(now)
  const target = startOfDay(new Date(due))
  const step = target >= today ? 1 : -1
  let count = 0
  const cursor = new Date(today)
  while (cursor.getTime() !== target.getTime()) {
    cursor.setDate(cursor.getDate() + step)
    if (isBusinessDay(cursor)) count += step
  }
  return count
}

// Tokens only. A ramp from plain to red as the date closes in: plain while far
// off, amber as it nears, a light red on its last working day, full red once
// the server calls it late. A past date the server does not call late (a
// company never contacted) is a queue position, shown plain.
export type DueLevel = "overdue" | "today" | "soon" | "near" | "plain"

export const dueTone = cva("", {
  variants: {
    level: {
      overdue: "bg-destructive/15 text-destructive",
      today: "bg-destructive/5 text-destructive",
      soon: "bg-warning/15 text-warning-text",
      near: "bg-warning/5 text-warning-text",
      plain: "",
    } satisfies Record<DueLevel, string>,
  },
})

export function dueLevel(due: string, overdue: boolean): DueLevel {
  if (overdue) return "overdue"
  if (new Date(due).getTime() < Date.now()) return "plain"
  const left = businessDaysUntil(due)
  if (left <= 0) return "today"
  if (left <= DUE_SOON_DAYS) return "soon"
  if (left <= DUE_HEADS_UP_DAYS) return "near"
  return "plain"
}

// A day picked in the calendar plus a "HH:MM" time, as one instant. The time
// falls back rather than defaulting to midnight, and an empty day means "no
// deadline at all", which is a plain note.
export function composeDueAt(
  day: Date | undefined,
  time: string,
): string | null {
  if (!day) return null
  const [hours, minutes] = (time || DEFAULT_DUE_TIME).split(":")
  const due = new Date(day)
  due.setHours(Number(hours) || DEFAULT_DUE_HOUR, Number(minutes) || 0, 0, 0)
  return due.toISOString()
}

export function formatDue(due: string | null, locale: "FR" | "EN"): string {
  if (!due) return ""
  return new Date(due).toLocaleString(locale === "FR" ? "fr-FR" : "en-US", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
}

// The locale CalendarNatural parses in ("tomorrow" vs "demain").
export function calendarLocale(locale: "FR" | "EN"): "fr-FR" | "en-US" {
  return locale === "FR" ? "fr-FR" : "en-US"
}
